import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import type { ShiftWithTotals } from '@/features/shifts/schemas';
import type { ServiceDTO } from '@/features/services/api';
import { useShiftMutations } from '@/features/shifts/hooks';
import { cn } from '@/lib/utils';

interface DayModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dateISO: string | null;
  shifts: ShiftWithTotals[];
  services: ServiceDTO[];
  cnpjId: string | null;
  onSchedule: (dateISO: string) => void;
}

export function DayModal({
  open,
  onOpenChange,
  dateISO,
  shifts,
  services,
  cnpjId,
  onSchedule,
}: DayModalProps) {
  const navigate = useNavigate();
  const { show } = useToast();
  const { start, remove } = useShiftMutations(cnpjId);

  if (!dateISO) return null;
  const date = parseISO(dateISO);
  const serviceMap = new Map(services.map((s) => [s.id, s]));
  const dayShifts = shifts.filter((s) => s.shift.date === dateISO);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="capitalize">
            {format(date, "EEEE, dd 'de' LLLL", { locale: ptBR })}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {dayShifts.length === 0 ? (
            <p className="text-sm text-(--color-fg-muted)">
              Nenhum plantão agendado neste dia.
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {dayShifts.map((s) => {
                const svc = serviceMap.get(s.shift.serviceId);
                return (
                  <li
                    key={s.shift.id}
                    className="rounded-md border border-(--color-border) bg-(--color-surface) p-3"
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className="mt-0.5 inline-block h-3 w-3 shrink-0 rounded-sm"
                        style={{ backgroundColor: svc?.color ?? '#888' }}
                        aria-hidden="true"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-medium">{svc?.name ?? 'Serviço'}</p>
                          <StatusBadge status={s.shift.status} />
                        </div>
                        <p className="mt-0.5 text-sm text-(--color-fg-muted)">
                          Total: {brl(s.totals.total)} ({s.totals.itemsCount} itens)
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {s.shift.status === 'PENDING' && (
                        <Button
                          size="sm"
                          onClick={() =>
                            start.mutate(s.shift.id, {
                              onSuccess: () => {
                                onOpenChange(false);
                                navigate(`/shift/${s.shift.id}`);
                              },
                              onError: (e) =>
                                show({
                                  title: 'Não foi possível iniciar',
                                  description: e instanceof Error ? e.message : '',
                                  variant: 'error',
                                }),
                            })
                          }
                        >
                          Iniciar
                        </Button>
                      )}
                      {s.shift.status === 'ACTIVE' && (
                        <Button
                          size="sm"
                          onClick={() => {
                            onOpenChange(false);
                            navigate(`/shift/${s.shift.id}`);
                          }}
                        >
                          Abrir Produção
                        </Button>
                      )}
                      {s.shift.status === 'DONE' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            onOpenChange(false);
                            navigate(`/shift/${s.shift.id}`);
                          }}
                        >
                          Ver resumo
                        </Button>
                      )}
                      {s.shift.status === 'PENDING' && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            if (!confirm('Excluir este plantão?')) return;
                            remove.mutate(s.shift.id, {
                              onError: (e) =>
                                show({
                                  title: 'Erro ao excluir',
                                  description: e instanceof Error ? e.message : '',
                                  variant: 'error',
                                }),
                            });
                          }}
                        >
                          Excluir
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          <Button onClick={() => onSchedule(dateISO)}>Agendar plantão</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatusBadge({ status }: { status: 'PENDING' | 'ACTIVE' | 'DONE' }) {
  const label = status === 'PENDING' ? 'Pendente' : status === 'ACTIVE' ? 'Ativo' : 'Encerrado';
  const cls =
    status === 'PENDING'
      ? 'bg-(--color-warning)/20 text-(--color-warning)'
      : status === 'ACTIVE'
        ? 'bg-(--color-accent)/20 text-(--color-accent)'
        : 'bg-(--color-success)/20 text-(--color-success)';
  return (
    <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', cls)}>{label}</span>
  );
}

function brl(v: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
}
